import type { AllAgent } from "@src/components/cards/prompt-card/queries/get-supported-agents";
import { isImageGenerationModel } from "@src/components/cards/utils/model-presentation";
import {
	Modality,
	SupportedAgent,
	type SupportedModel,
	SupportedModelCapability,
} from "@src/gql/graphql";
import { AGENT_CHAT_LLM_MODEL_STORAGE_KEY } from "./constants";

export type ChatToolModel = {
	model: SupportedModel;
	modelId: string;
};

function isImageOnlyChatModel(model: AllAgent["models"][number]) {
	if (isImageGenerationModel(model.model)) {
		return true;
	}

	const outputs = model.outputModalities ?? [];
	if (outputs.includes(Modality.Image) && !outputs.includes(Modality.Text)) {
		return true;
	}

	const agents = model.compatibleAgents;
	return (
		agents.includes(SupportedAgent.ImageMaker) &&
		!agents.includes(SupportedAgent.AiAssistant)
	);
}

function isToolCapableChatModel(model: AllAgent["models"][number]) {
	if (model.isRegionRestricted || !model.isTierAvailable) {
		return false;
	}

	if (isImageOnlyChatModel(model)) {
		return false;
	}

	return model.capabilities.includes(SupportedModelCapability.Tools);
}

export function collectToolCapableChatModels(
	allAgents: AllAgent[] | undefined,
): ChatToolModel[] {
	if (!allAgents || allAgents.length === 0) {
		return [];
	}

	const byModelId = new Map<string, ChatToolModel>();
	for (const agent of allAgents) {
		for (const model of agent.models) {
			if (!isToolCapableChatModel(model)) {
				continue;
			}

			if (byModelId.has(model.modelId)) {
				continue;
			}

			byModelId.set(model.modelId, {
				model: model.model,
				modelId: model.modelId,
			});
		}
	}

	return [...byModelId.values()];
}

export function resolveChatLlmModel(
	models: ChatToolModel[],
	preferredModelId: string | null | undefined,
	fallbackModelId: string,
): string {
	if (preferredModelId && models.some((m) => m.modelId === preferredModelId)) {
		return preferredModelId;
	}

	if (models.some((m) => m.modelId === fallbackModelId)) {
		return fallbackModelId;
	}

	return models[0]?.modelId ?? fallbackModelId;
}

export function readStoredChatLlmModel(): string | null {
	try {
		return window.localStorage.getItem(AGENT_CHAT_LLM_MODEL_STORAGE_KEY);
	} catch {
		return null;
	}
}

export function writeStoredChatLlmModel(modelId: string): void {
	try {
		window.localStorage.setItem(AGENT_CHAT_LLM_MODEL_STORAGE_KEY, modelId);
	} catch {
		// Ignore quota / private-mode failures.
	}
}
